import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import useVelocityClient from 'src/hooks/useVelocityClient';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { BigNum, BN, OrderType, PRICE_PRECISION } from '@velocity-exchange/sdk';
import {
	createEditOrderIx,
	CreateEditOrderIxParams,
	ENUM_UTILS,
	UIMarket,
	UISerializableOrder,
} from '@velocity-exchange/common';
import { notify } from 'src/utils/notifications';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { TRADE_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/tradePrep';
import { ORDER_PREP_UTILS } from 'src/stores/VelocityStore/actions/actionHelpers/orderPrep';
import { SlippageTolerance } from 'src/@types/types';
import { useTxErrorHandler } from 'src/hooks/velocityActions/transactions/useTxErrorHandler';
import { runOrderActionToast } from 'src/hooks/velocityActions/transactions/runOrderActionToast';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';
import { useUserProtectionSlotClock } from 'src/hooks/useSlotClock';

type EditableNonMarketOrderParams = {
	baseAmount?: BigNum;
	limitPrice?: BigNum;
	triggerPrice?: BigNum;
	oraclePriceOffset?: number;
};

export const useEditNonMarketOrder = () => {
	const velocityClient = useVelocityClient();
	const isGeoBlocked = useVelocityStore((s) => s.isGeoblocked);
	const currentUserAccount = useCurrentUserAccount();

	const getTxParams = useGetTxParams();
	const getOraclePrice = useGetOraclePriceForMarket();
	const handleTxError = useTxErrorHandler();
	const { slotDurationMs } = useUserProtectionSlotClock();

	const editNonMarketOrder = useAutoSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				existingOrder: UISerializableOrder,
				slippageTolerance: SlippageTolerance,
				newOrderParams: EditableNonMarketOrderParams
			) => {
				if (isGeoBlocked) {
					notify({
						type: 'error',
						message: 'Geoblocked',
						description:
							'You are not allowed to use Velocity from a restricted territory',
						lengthMs: 5000,
					});
					return false;
				}

				invariant(velocityClient, 'driftClient is required');
				invariant(currentUserAccount?.client, 'user client is required');

				const isMarketOrder =
					ENUM_UTILS.match(existingOrder.orderType, OrderType.MARKET) ||
					ENUM_UTILS.match(existingOrder.orderType, OrderType.ORACLE);
				invariant(!isMarketOrder, 'Market orders cannot be edited');

				const uiMarket = new UIMarket(
					existingOrder.marketIndex,
					existingOrder.marketType
				);
				const isLimitOrder = ENUM_UTILS.match(
					existingOrder.orderType,
					OrderType.LIMIT
				);
				// Capture the narrowed client here; the `run` closure below re-widens
				// `currentUserAccount?.client` back to `User | undefined`.
				const userClient = currentUserAccount.client;

				const result = await runOrderActionToast({
					toastId,
					isAutoSigned,
					pendingMessage: 'Editing order...',
					run: async () => {
						const editOrderOptions = {
							newBaseAmount: newOrderParams.baseAmount?.val,
							newLimitPrice: newOrderParams.limitPrice?.val,
							newOraclePriceOffset: newOrderParams.oraclePriceOffset
								? new BN(
										Math.round(
											newOrderParams.oraclePriceOffset *
												PRICE_PRECISION.toNumber()
										)
									)
								: undefined,
							newTriggerPrice: newOrderParams.triggerPrice?.val,
						};

						const settings = syncGetCurrentSettings();
						const auctionStartPriceOffset =
							TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
								settings?.auctionStartPriceOffset as number | 'marketBased',
								uiMarket.marketId
							);
						const oraclePrice = getOraclePrice(uiMarket.marketId);

						let limitAuctionOrderConfig:
							| CreateEditOrderIxParams['limitAuctionOrderConfig']
							| undefined = undefined;

						const isOracleLimitOrder =
							existingOrder.oraclePriceOffset &&
							!existingOrder.oraclePriceOffset.eqZero();
						if (isLimitOrder && !isOracleLimitOrder) {
							const marketAccount = uiMarket.isPerp
								? velocityClient.getPerpMarketAccount(existingOrder.marketIndex)
								: velocityClient.getSpotMarketAccount(
										existingOrder.marketIndex
									);
							invariant(marketAccount, 'marketAccount is required');
							invariant(newOrderParams.limitPrice, 'limitPrice is required');

							const optionalAuctionParamsInputs =
								ORDER_PREP_UTILS.getOptionalAuctionParamsInputs({
									slippageTolerance,
									marketAccount,
									overrideIsOracleOrder: {
										isOracleOrder: false, // we don't use oracle order parameters for limit auction orders
									},
									uiMarket,
									maxLeverageSelected: false,
									slotDuration: slotDurationMs,
								});
							limitAuctionOrderConfig = {
								orderType: 'limit',
								limitPrice: newOrderParams.limitPrice.val,
								limitAuction: {
									enable: true,
									dlobServerHttpUrl: Env.dlobServerHttpUrl,
									auctionStartPriceOffset,
									oraclePrice: oraclePrice.val,
									optionalLimitAuctionParams: optionalAuctionParamsInputs,
								},
							};
						}

						const editOrderIx = await createEditOrderIx({
							velocityClient: velocityClient,
							user: userClient,
							orderId: existingOrder.orderId,
							editOrderParams: editOrderOptions,
							limitAuctionOrderConfig,
							mainSignerOverride: signerWallet.publicKey,
							slotDuration: slotDurationMs,
						});

						const txParams = getTxParams();
						await signerWallet.signAndSendTransaction(editOrderIx, txParams);

						return true;
					},
					success: { message: 'Order edited.' },
					handleError: handleTxError,
					errorLogLabel: 'Error editing non-market order',
				});

				return result ?? false;
			},
			[
				isGeoBlocked,
				velocityClient,
				currentUserAccount,
				getTxParams,
				getOraclePrice,
				handleTxError,
				slotDurationMs,
			]
		)
	);

	return { editNonMarketOrder };
};
